import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { PaymentModal } from '@/components/payments/payment-modal';
import { useAuthStore } from '@/stores/auth-store';
import { useClaimsStore } from '@/stores/claims-store';
import { trackBeginCheckout } from '@/lib/analytics';

interface PurchasePackButtonProps {
  claimId: string;
  packType: 'council' | 'court';
  disabled?: boolean;
  disabledReason?: string;
  evidenceScore?: number;
  alreadyPurchased?: boolean;
  hasAuthorityAddress?: boolean;
  onPurchaseComplete?: () => void;
}

const PACK_PRICES = {
  council: { price: 35, label: 'Council Claim Pack' },
  court: { price: 0, label: 'Court Escalation Support' },
};

export function PurchasePackButton({
  claimId,
  packType,
  disabled,
  disabledReason,
  evidenceScore,
  alreadyPurchased,
  hasAuthorityAddress,
  onPurchaseComplete,
}: PurchasePackButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { accessToken } = useAuthStore();
  const { fetchPacks, fetchClaim } = useClaimsStore();

  const packInfo = PACK_PRICES[packType];
  const minScore = 70;
  const hasMinScore = evidenceScore != null && evidenceScore >= minScore;

  // The court pack is not exposed until the backend generates court-specific documents.
  if (packType === 'court') {
    return (
      <div className="space-y-2">
        <Button fullWidth disabled className="opacity-50">
          Court Escalation Support
        </Button>
        <p className="text-xs text-surface-600 text-center">
          Court document preparation is not currently available to purchase
        </p>
      </div>
    );
  }

  const handleSuccess = () => {
    setIsModalOpen(false);
    // Always refresh data when payment completes
    fetchPacks(claimId);
    fetchClaim(claimId);
    onPurchaseComplete?.();
  };

  // Already purchased
  if (alreadyPurchased) {
    return (
      <div className="space-y-2">
        <Button fullWidth disabled variant="secondary">
          <span className="flex items-center gap-2">
            <span className="text-success-500">✓</span>
            {packInfo.label} Purchased
          </span>
        </Button>
      </div>
    );
  }

  // Explicitly disabled with custom reason (e.g., Phase 4 feature)
  if (disabled && disabledReason) {
    return (
      <div className="space-y-2">
        <Button fullWidth disabled className="opacity-50">
          {packType === 'council' ? '📋' : '⚖️'} {packInfo.label} (£{packInfo.price})
        </Button>
        <p className="text-xs text-surface-500 text-center">
          {disabledReason}
        </p>
      </div>
    );
  }

  // Missing council address
  if (!hasAuthorityAddress) {
    return (
      <div className="space-y-2">
        <Button fullWidth disabled>
          Generate {packInfo.label} (£{packInfo.price})
        </Button>
        <p className="text-xs text-surface-600 text-center">
          Please add the responsible body's postal address above before generating a pack
        </p>
      </div>
    );
  }

  // Not enough evidence
  if (!hasMinScore) {
    return (
      <div className="space-y-2">
        <Button fullWidth disabled>
          Generate {packInfo.label} (£{packInfo.price})
        </Button>
        <p className="text-xs text-surface-600 text-center">
          Evidence score must be at least {minScore}% to generate a pack
          {evidenceScore != null && ` (currently ${evidenceScore}%)`}
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="space-y-2">
        <Button
          fullWidth
          onClick={() => {
            trackBeginCheckout(packType, packInfo.price);
            setIsModalOpen(true);
          }}
          disabled={disabled || !accessToken}
        >
          📋 Generate {packInfo.label} (£{packInfo.price})
        </Button>
        
        <p className="text-xs text-surface-500 text-center">
          Professional claim letter, evidence bundle & submission guide
        </p>
      </div>

      {accessToken && (
        <PaymentModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSuccess={handleSuccess}
          claimId={claimId}
          packType={packType}
        />
      )}
    </>
  );
}
